import type { TextNode } from "@/document/model/nodes";
import { resolvedLineHeight, resolvedTextStyleAt } from "@/document/model/text";
import type { Rect } from "@/lib/math/rect";
import { getCachedTextLayout } from "./canvasKitText";

export function textVisualBounds(node: TextNode): Rect {
  const metrics = typeof document === "undefined" ? fallbackTextVisualMetrics(node) : layoutTextVisualMetrics(node);
  return {
    x: node.bounds.x,
    y: node.bounds.y + verticalOffset(node.bounds.height, metrics.layoutHeight, node.text.style.verticalAlign),
    width: metrics.width,
    height: metrics.visualHeight,
  };
}

function layoutTextVisualMetrics(node: TextNode): { width: number; layoutHeight: number; visualHeight: number } {
  const layout = getCachedTextLayout(node, 1);
  const lineVisualBottoms = new Map<number, number>();
  for (const run of layout.runs) {
    const current = lineVisualBottoms.get(run.y) ?? run.y;
    lineVisualBottoms.set(run.y, Math.max(current, run.y + run.style.fontSize));
  }

  return {
    width: node.text.textLayout === "AUTO_WIDTH" ? layout.width : node.bounds.width,
    layoutHeight: layout.height,
    visualHeight: Math.max(1, ...lineVisualBottoms.values()),
  };
}

function fallbackTextVisualMetrics(node: TextNode): { width: number; layoutHeight: number; visualHeight: number } {
  let sourceIndex = 0;
  let layoutHeight = 0;
  let visualHeight = 0;

  for (const line of node.text.characters.split("\n")) {
    const styles = line.length === 0
      ? [node.text.style]
      : Array.from({ length: line.length }, (_, offset) => resolvedTextStyleAt(node.text, sourceIndex + offset));
    const lineHeight = Math.max(...styles.map(style => resolvedLineHeight(style)));
    const fontSize = Math.max(...styles.map(style => style.fontSize));
    visualHeight = Math.max(visualHeight, layoutHeight + fontSize);
    layoutHeight += lineHeight;
    sourceIndex += line.length + 1;
  }

  return {
    width: node.bounds.width,
    layoutHeight: Math.max(1, layoutHeight),
    visualHeight: Math.max(1, visualHeight),
  };
}

function verticalOffset(boxHeight: number, textHeight: number, align: TextNode["text"]["style"]["verticalAlign"]): number {
  if (align === "CENTER") {
    return Math.max(0, (boxHeight - textHeight) / 2);
  }
  if (align === "BOTTOM") {
    return Math.max(0, boxHeight - textHeight);
  }
  return 0;
}
